import { FIGURE_SVG_EDITOR } from '../../utils/roles';
import React from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { boxGroups, boxEntries, boxMarkerPrefix } from './boxRules';

/**
 * BlockRenderer — staging 전용 Block/Run 포맷 읽기 렌더러 (FE-4).
 *
 * 본문·풀이는 아직 Lexical이 아니라 staging Block/Run 포맷(승격 시 백엔드가 Lexical 변환).
 * latex 런은 bare LaTeX($ 구분자 없음) → katex.renderToString(ProblemDetailPanel 관례).
 * 지시서 원문이 포맷 정의를 일부 유실한 상태라 필드명은 방어적으로 수용한다
 * (run: t|type|kind + v|value|text|content, block: runs|content|children). 렌더 불가 시
 * code 스타일 원문 표시로 강등 — "검수 가능 수준 우선" 원칙.
 */

export const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];

// image 블록 실표시용 페이지 이미지 컨텍스트 — { url } (본문 kind=1 소스 페이지의 presigned).
// 별도 자산 API 없음: bbox(0~1 페이지 좌표)로 페이지 이미지를 클라이언트 크롭해 보여준다(BE 회신 Q2).
// 제공자가 없거나 URL이 아직 없으면 [그림] 뱃지로 폴백.
export const PageImageContext = React.createContext(null);

// bbox 크롭 표시 — background 상대좌표 방식: bg-size/position이 컨테이너 % 기준이라 표 셀 같은
// 좁은 자리에서도 크롭 영역이 잘리지 않고 통째로 축소된다(픽셀 오프셋 방식의 오크롭 결함 대체).
// aspect-ratio에 필요한 페이지 자연 크기만 offscreen img로 1회 측정. 호출부(ImageBlockView)가
// key={src+bbox}로 리마운트시켜 src·bbox가 바뀌면 nat/failed가 반드시 리셋된다.
function CroppedPageImage({ src, bbox, maxWidth = 420 }) {
  const [nat, setNat] = React.useState(null); // { w, h } 페이지 자연 크기
  const [failed, setFailed] = React.useState(false);
  const [x0, y0, x1, y1] = bbox;
  const cw = Math.min(1, Math.max(0, x1 - x0));
  const ch = Math.min(1, Math.max(0, y1 - y0));
  if (failed || cw === 0 || ch === 0) {
    return <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">[그림]</span>;
  }
  if (!nat) {
    return (
      <span className="relative inline-block h-16 w-40 max-w-full overflow-hidden rounded bg-gray-100 align-middle">
        <img
          src={src}
          alt=""
          className="invisible absolute"
          onLoad={(e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
          onError={() => setFailed(true)}
        />
      </span>
    );
  }
  const cropW = cw * nat.w;
  const cropH = ch * nat.h;
  // CSS 백분율 bg-position 의미론: p%는 (컨테이너-이미지)×p 지점 정렬 → 원하는 -x0 오프셋은 x0/(1-cw).
  // cw=1(전폭 크롭)이면 오프셋이 무의미하므로 0으로 고정(0 나눗셈 방지).
  const posX = cw >= 1 ? 0 : (x0 / (1 - cw)) * 100;
  const posY = ch >= 1 ? 0 : (y0 / (1 - ch)) * 100;
  return (
    <span
      role="img"
      aria-label="문제 그림"
      className="my-1 block w-full overflow-hidden rounded border border-gray-100"
      style={{
        maxWidth: Math.min(maxWidth, cropW),
        aspectRatio: `${cropW} / ${cropH}`,
        backgroundImage: `url("${src}")`,
        backgroundSize: `${100 / cw}% auto`,
        backgroundPosition: `${posX}% ${posY}%`,
        backgroundRepeat: 'no-repeat',
      }}
    />
  );
}

// table 블록: { kind:'table', rows: [[Cell…]…] }, Cell = { content:[Block…], colSpan?, rowSpan?, header? }
function TableBlockView({ block }) {
  const rows = Array.isArray(block.rows) ? block.rows : [];
  if (rows.length === 0) {
    return <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-600">{JSON.stringify(block, null, 2)}</pre>;
  }
  return (
    <div className="my-1 overflow-x-auto">
      <table className="border-collapse text-sm">
        <tbody>
          {rows.map((cells, ri) => (
            <tr key={ri}>
              {(Array.isArray(cells) ? cells : []).map((cell, ci) => {
                const Tag = cell?.header ? 'th' : 'td';
                return (
                  <Tag
                    key={ci}
                    colSpan={cell?.colSpan > 1 ? cell.colSpan : undefined}
                    rowSpan={cell?.rowSpan > 1 ? cell.rowSpan : undefined}
                    className={`border border-gray-300 px-2 py-1 text-center align-middle ${cell?.header ? 'bg-gray-50 font-medium' : ''}`}
                  >
                    <Blocks blocks={cell?.content} inline />
                  </Tag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// list 블록: { kind:'list', ordered: bool, items: [[Block…]…] }
function ListBlockView({ block }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (items.length === 0) {
    return <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-600">{JSON.stringify(block, null, 2)}</pre>;
  }
  const Tag = block.ordered ? 'ol' : 'ul';
  return (
    <Tag className={`my-1 space-y-0.5 pl-5 text-sm ${block.ordered ? 'list-decimal' : 'list-disc'}`}>
      {items.map((blocks, i) => (
        <li key={i}><Blocks blocks={blocks} inline /></li>
      ))}
    </Tag>
  );
}

function renderLatex(value, displayMode = false) {
  try {
    return katex.renderToString(String(value), { throwOnError: false, displayMode });
  } catch (e) {
    return null;
  }
}

function RunView({ run }) {
  if (run == null) return null;
  if (typeof run === 'string') return <span>{run}</span>;
  const type = String(run.t ?? run.type ?? run.kind ?? 'text');
  if (type.includes('latex') || type.includes('math')) {
    // math Run 계약: { kind:'math', latex } — latex 필드가 정본(수정요청 F1)
    const value = run.latex ?? run.v ?? run.value ?? run.text ?? run.content ?? '';
    const html = renderLatex(value);
    if (html) {
      // eslint-disable-next-line react/no-danger
      return <span dangerouslySetInnerHTML={{ __html: html }} />;
    }
    return <code className="rounded bg-gray-100 px-1 text-[0.9em] text-gray-700">{String(value)}</code>;
  }
  if (type.includes('blank')) {
    // 빈칸 Run 계약: { kind:'blank', label } — (가)/(나) 표식 유지(수정요청 F3, 백엔드 텍스트 변환과 동일 표기)
    const label = run.label ?? run.v ?? run.value ?? '';
    return <span className="mx-0.5 font-medium text-gray-700">( {String(label)} )</span>;
  }
  // 이미지 run — 계약(Q1)의 image 는 블록이지만, run 자리에 섞여 와도 소실시키지 않는다
  if (type.includes('image')) {
    return <ImageBlockView block={run} />;
  }
  const value = run.v ?? run.value ?? run.text ?? run.content;
  if (value == null) {
    // 알 수 없는 run 형태 — 빈 span 무음 소실 금지(파일 머리 원칙: 렌더 불가 시 원문 강등)
    return <code className="rounded bg-gray-100 px-1 text-[0.9em] text-gray-600">{JSON.stringify(run)}</code>;
  }
  return <span>{String(value)}</span>;
}

function BlockView({ block }) {
  if (block == null) return null;
  if (typeof block === 'string') return <div className="leading-relaxed">{block}</div>;
  const kind = String(block.kind ?? block.type ?? '');
  // 독립(가운데) 수식 블록 계약: { kind:'math', latex } → displayMode 렌더(수정요청 F2).
  // latex가 비어 있으면(계약 위반 형태) 아래 runs 폴백으로 계속 — 빈 div로 소실 방지.
  if (kind.includes('math')) {
    const latex = block.latex ?? block.v ?? block.value ?? '';
    if (String(latex) !== '') {
      const html = renderLatex(latex, true);
      if (html) {
        // eslint-disable-next-line react/no-danger
        return <div className="my-1 overflow-x-auto text-center" dangerouslySetInnerHTML={{ __html: html }} />;
      }
      return <code className="block rounded bg-gray-100 px-1 py-0.5 text-[0.9em] text-gray-700">{String(latex)}</code>;
    }
  }
  // 이미지 블록 — 페이지 presigned + bbox 클라이언트 크롭 실표시(BE 회신 Q2). 컨텍스트(페이지 URL)나
  // bbox가 없으면 종전 플레이스홀더 뱃지로 폴백.
  if (kind.includes('image')) {
    return <ImageBlockView block={block} />;
  }
  if (kind === 'table') {
    return <TableBlockView block={block} />;
  }
  if (kind === 'list') {
    return <ListBlockView block={block} />;
  }
  // 발문 안 상자(서버 대장 B1 — 모델이 최상위 box 대신 stem 안 제자리에 낸 모양). 종전엔 모르는 블록으로
  // JSON 원문이 그대로 찍혔다. 그 자리에 테두리 상자로 그린다(F3 — 규칙은 boxRules, 서버와 같다).
  if (kind === 'box') {
    return <BoxView group={[block]} />;
  }
  const runs = Array.isArray(block.runs) ? block.runs
    : Array.isArray(block.content) ? block.content
      : Array.isArray(block.children) ? block.children
        : null;
  if (runs) {
    return (
      <div className="leading-relaxed">
        {runs.map((r, i) => <RunView key={i} run={r} />)}
      </div>
    );
  }
  // 알 수 없는 블록 형태 — 원문 강등 표시(검수 가능 수준 우선)
  return <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-600">{JSON.stringify(block, null, 2)}</pre>;
}

function ImageBlockView({ block }) {
  const pageImage = React.useContext(PageImageContext);
  const bbox = Array.isArray(block.bbox) && block.bbox.length === 4 ? block.bbox : null;
  // 서버가 렌더해 실어 준 도판(BE 부록 2026-08-30): image 블록에 spec 이 있으면 `svg`(data URI)와
  // 픽셀 크기가 함께 온다. **가장 먼저 본다** — 이게 있으면 bbox 크롭보다 정확한 그림이다.
  // 렌더 실패면 `svg:null` + `issues` 라 여기 걸리지 않고 아래 폴백으로 내려간다.
  // 저장은 spec 만 하므로 이 값들은 되돌려 보내지 않아도 된다(보내도 무시된다).
  const source = FIGURE_SVG_EDITOR && typeof block.src === 'string' && block.src.trim() ? block.src : block.svg;
  if (typeof source === 'string' && source !== '') {
    return (
      <img
        src={source}
        alt={block.alt || '문제 그림'}
        className="my-1 block max-w-full"
        style={{ width: Number(block.width) > 0 ? block.width : undefined }}
      />
    );
  }
  if (pageImage?.url && bbox) {
    // key로 리마운트 — 문제 전환·재OCR로 src/bbox가 바뀌면 측정·실패 상태를 반드시 리셋
    return <CroppedPageImage key={`${pageImage.url}|${bbox.join(',')}`} src={pageImage.url} bbox={bbox} />;
  }
  return (
    <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">[그림]</span>
  );
}

/** Block[] 렌더 — inline: 표 셀·리스트 항목처럼 여백 없는 자리용 */
export function Blocks({ blocks, inline = false }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <div className={inline ? 'text-sm text-gray-900' : 'space-y-1 text-sm text-gray-900'}>
      {blocks.map((b, i) => <BlockView key={i} block={b} />)}
    </div>
  );
}

/**
 * 상자 하나(묶음) — 테두리 안에 항목마다 「마커 + 내용」. 마커는 서버 규칙대로(boxMarkerPrefix):
 * 이미 구두점으로 끝나면 덧붙이지 않고(「ㄱ..」 없음), 마커가 없으면 앞에 「.」을 찍지 않는다(종전엔 늘 찍었다).
 */
function BoxView({ group, label = null }) {
  const entries = boxEntries(group);
  if (entries.length === 0) return null;
  return (
    <div className="rounded-xl border border-gray-200 p-3">
      {label && <div className="mb-1 text-xs font-medium text-gray-400">{label}</div>}
      <div className="space-y-1">
        {entries.map((e, i) => {
          const prefix = boxMarkerPrefix(e?.marker);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="flex gap-2 text-sm">
              {prefix && <span className="shrink-0 font-medium text-gray-600">{prefix.trim()}</span>}
              <div className="min-w-0 flex-1"><Blocks blocks={e?.content} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * body_payload 렌더: { stem: Block[], choices: [{no, content: Block[]}](①~⑩ · 초과는 (n)),
 *                     box: [{marker, content}](<보기> ㄱㄴㄷ), answerType, points }
 */
export function BodyPayloadView({ payload }) {
  if (!payload || typeof payload !== 'object') {
    return <div className="text-sm text-gray-400">본문 데이터가 없습니다.</div>;
  }
  const choices = Array.isArray(payload.choices) ? payload.choices : [];
  // 최상위 상자 — 구형(마커 항목들)은 한 상자, 신형(items·마커 없는 항목)은 항목마다 한 상자(서버 box_groups).
  // 머리의 「<보기>」 표지는 종전 그대로 최상위 상자에만 둔다(발문 안 상자는 지면에 표지가 없다).
  const groups = boxGroups(payload.box);
  return (
    <div className="space-y-3">
      <Blocks blocks={payload.stem} />
      {groups.map((g, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <BoxView key={i} group={g} label="<보기>" />
      ))}
      {choices.length > 0 && (
        // choices_columns(신설): 지면에서 선지가 한 행에 몇 개씩인지(1~5). 2 이상이면 격자 미리보기 —
        // 승격 시 layout-container로 가는 모양을 검수 화면에서도 비슷하게 보여준다.
        <div
          className={Number(payload.choices_columns) >= 2 ? 'grid gap-x-4 gap-y-1' : 'space-y-1'}
          style={Number(payload.choices_columns) >= 2
            ? { gridTemplateColumns: `repeat(${Math.min(5, Number(payload.choices_columns))}, minmax(0, 1fr))` }
            : undefined}
        >
          {choices.map((c, i) => (
            <div key={i} className="flex gap-2 text-sm">
              {/* 마커 1.21em — 기존 문제은행은 원문자를 수식 노드로 저장해 KaTeX 기본 배율(1.21em)로
                  그려진다. 본문 크기 그대로면 동그라미 속 숫자가 뭉개져 작아 보인다. KaTeX를 거치지
                  않는 이유: 원문자 메트릭이 없어 폴백 폰트+콘솔 경고만 얻고 크기 효과는 같다. */}
              <span className="shrink-0 text-gray-600" style={{ fontSize: '1.21em', lineHeight: 1.2 }}>
                {CIRCLED[(c?.no ?? i + 1) - 1] || `(${c?.no ?? i + 1})`}
              </span>
              <div className="min-w-0 flex-1"><Blocks blocks={c?.content} /></div>
            </div>
          ))}
        </div>
      )}
      {(payload.answerType || payload.points != null) && (
        <div className="text-xs text-gray-400">
          {payload.answerType ? `유형: ${payload.answerType}` : ''}
          {payload.answerType && payload.points != null ? ' · ' : ''}
          {payload.points != null ? `배점: ${payload.points}` : ''}
        </div>
      )}
    </div>
  );
}

/** solution_payload 렌더: { content: Block[], final_value?: string } */
export function SolutionPayloadView({ payload }) {
  if (!payload || typeof payload !== 'object') {
    return <div className="text-sm text-gray-400">풀이가 없습니다.</div>;
  }
  return (
    <div className="space-y-2">
      <Blocks blocks={payload.content} />
      {payload.final_value != null && payload.final_value !== '' && (
        <div className="text-sm text-gray-700">
          <span className="font-medium text-gray-500">최종값: </span>{String(payload.final_value)}
        </div>
      )}
    </div>
  );
}

export default Blocks;
